import {
  AcceptInvitationRequestSchema,
  InvitationSchema,
  PasswordSchema,
} from '@fundline/shared/schemas/members';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TextField } from '../../components/ui/Field.tsx';
import { Brand, CenteredPage, Spinner } from '../../components/ui/Layout.tsx';
import { useAuth } from '../../lib/auth.tsx';
import { captureAuthRedirectError } from '../../lib/auth-redirect.ts';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { ApiRequestError, workerApi } from '../../lib/worker-api.ts';

const route = getRouteApi('/welcome');

/**
 * Landing page of invite links (`/welcome?ws=<workspace>`). Supabase has already signed the invitee in
 * from the link; here they choose a name and password, and the worker activates the membership.
 */
export function WelcomePage() {
  const { ws } = route.useSearch();
  const auth = useAuth();
  const [redirectError] = useState(captureAuthRedirectError);

  if (redirectError) {
    return (
      <CenteredPage>
        <Brand />
        <h1 className="title">This link cannot be used</h1>
        <Alert tone="warning">{redirectError.message}</Alert>
        <p className="callout text-ink-muted">
          Already set a password?{' '}
          <Link to="/login" className="font-medium text-ink underline">
            Sign in
          </Link>
        </p>
      </CenteredPage>
    );
  }

  if (auth.status !== 'signed_in' || !auth.claims) {
    return (
      <CenteredPage>
        <Brand />
        <h1 className="title">Join Fundline</h1>
        <Alert>Open the invite link from your admin again, or sign in if you already joined.</Alert>
        <Link to="/login" className="callout font-medium text-ink underline">
          Sign in
        </Link>
      </CenteredPage>
    );
  }

  if (!ws) {
    return (
      <CenteredPage>
        <Brand />
        <Alert tone="warning">
          This page needs the full link from your invitation. Ask your admin for a new invite link.
        </Alert>
      </CenteredPage>
    );
  }

  return (
    <AcceptInvitation workspaceId={ws} userId={auth.claims.userId} email={auth.claims.email} />
  );
}

function AcceptInvitation({
  workspaceId,
  userId,
  email,
}: {
  workspaceId: string;
  userId: string;
  email: string | null;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const invitation = useQuery({
    queryKey: queryKeys.invitation(workspaceId, userId),
    queryFn: () => workerApi(`/workspaces/${workspaceId}/invitation`, { schema: InvitationSchema }),
    retry: false,
  });

  const [fullName, setFullName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const enter = async () => {
    await auth.refresh(); // the new token carries the workspace and role
    await navigate({ to: '/' });
  };

  const accept = useMutation({
    mutationFn: async (input: { fullName: string; password: string }) => {
      const { error } = await supabase().auth.updateUser({ password: input.password });
      if (error) throw new Error(`Couldn't set your password: ${error.message}`);
      await workerApi(`/workspaces/${workspaceId}/invitation/accept`, {
        method: 'POST',
        body: { fullName: input.fullName },
        schema: InvitationSchema,
      });
      await enter();
    },
  });

  if (invitation.isPending) {
    return (
      <CenteredPage>
        <Spinner label="Opening your invitation" />
      </CenteredPage>
    );
  }

  if (invitation.isError) {
    const err = invitation.error;
    return (
      <CenteredPage>
        <Brand />
        <h1 className="title">Invitation not found</h1>
        <Alert tone="warning">
          {err instanceof ApiRequestError ? err.message : "Couldn't open the invitation."}
        </Alert>
        <Button variant="secondary" onClick={() => void auth.signOut()}>
          Sign out
        </Button>
      </CenteredPage>
    );
  }

  const inv = invitation.data;
  if (inv.status === 'active') {
    return (
      <CenteredPage>
        <Brand />
        <h1 className="title">You are already a member</h1>
        <p className="callout text-ink-muted">You have joined {inv.workspaceName}.</p>
        <Button onClick={() => void enter()}>Continue</Button>
      </CenteredPage>
    );
  }

  const nameValue = fullName ?? inv.fullName;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    const name = AcceptInvitationRequestSchema.shape.fullName.safeParse(nameValue);
    if (!name.success) errors.fullName = name.error.issues[0]?.message ?? 'Enter your name';
    const pw = PasswordSchema.safeParse(password);
    if (!pw.success) errors.password = pw.error.issues[0]?.message ?? 'Choose a password';
    else if (password !== confirm) errors.confirm = 'The passwords do not match';
    setFieldErrors(errors);
    if (name.success && pw.success && password === confirm) {
      accept.mutate({ fullName: name.data, password: pw.data });
    }
  }

  return (
    <CenteredPage>
      <Brand />
      <div>
        <h1 className="title">Join {inv.workspaceName}</h1>
        <p className="mt-1 callout text-ink-muted">
          {inv.invitedByName ? `${inv.invitedByName} invited you` : 'You are invited'} as{' '}
          {inv.role === 'admin' ? 'an admin' : 'a rep'}
          {email ? ` (${email})` : ''}. Choose your name and a password to finish.
        </p>
      </div>
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <TextField
          label="Your name"
          name="name"
          autoComplete="name"
          value={nameValue}
          onChange={(e) => setFullName(e.target.value)}
          error={fieldErrors.fullName}
        />
        <TextField
          label="Password"
          type="password"
          name="new-password"
          autoComplete="new-password"
          hint="At least 10 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
        />
        <TextField
          label="Confirm password"
          type="password"
          name="confirm-password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={fieldErrors.confirm}
        />
        {accept.isError ? <Alert tone="error">{accept.error.message}</Alert> : null}
        <Button type="submit" busy={accept.isPending}>
          Join workspace
        </Button>
      </form>
    </CenteredPage>
  );
}
